import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { ColorPicker } from '@/components/ui/color-picker';
// Icons removed - no icons used in UI
import { supportsPerPointColors } from '../capabilities';

interface PointControlsProps {
  plotlyJson?: any;
  onUpdate: (updateType: string, value: any) => void;
  selectedPoint?: { traceIndex: number; pointIndex: number } | null;
}

export function PointControls({ plotlyJson, onUpdate, selectedPoint }: PointControlsProps) {
  const [fillColor, setFillColor] = useState('#1f77b4');
  const [outlineColor, setOutlineColor] = useState('#333333');
  const [opacity, setOpacity] = useState(1);
  const [pointSize, setPointSize] = useState(8);
  const [outlineWidth, setOutlineWidth] = useState(1);

  // Debug logging
  console.log('🎯 PointControls - selectedPoint:', selectedPoint);

  // Update local state when a point is selected
  useEffect(() => {
    if (selectedPoint && plotlyJson?.data?.[selectedPoint.traceIndex]) {
      const trace = plotlyJson.data[selectedPoint.traceIndex];
      const pointIdx = selectedPoint.pointIndex;
      
      // Extract current point values if they exist
      if (trace.marker) {
        // Fill color
        if (Array.isArray(trace.marker.color)) {
          setFillColor(trace.marker.color[pointIdx] || '#1f77b4');
        } else if (typeof trace.marker.color === 'string') {
          setFillColor(trace.marker.color);
        }
        
        // Opacity
        if (Array.isArray(trace.marker.opacity)) {
          setOpacity(trace.marker.opacity[pointIdx] || 1);
        } else if (typeof trace.marker.opacity === 'number') {
          setOpacity(trace.marker.opacity);
        }
        
        // Size
        if (Array.isArray(trace.marker.size)) {
          setPointSize(trace.marker.size[pointIdx] || 8);
        } else if (typeof trace.marker.size === 'number') {
          setPointSize(trace.marker.size);
        }
        
        // Outline
        if (trace.marker.line) {
          if (Array.isArray(trace.marker.line.color)) {
            setOutlineColor(trace.marker.line.color[pointIdx] || '#333333');
          } else if (typeof trace.marker.line.color === 'string') {
            setOutlineColor(trace.marker.line.color);
          }
          
          if (Array.isArray(trace.marker.line.width)) {
            setOutlineWidth(trace.marker.line.width[pointIdx] || 1);
          } else if (typeof trace.marker.line.width === 'number') {
            setOutlineWidth(trace.marker.line.width);
          }
        }
      }
    }
  }, [selectedPoint, plotlyJson]);

  const handlePointUpdate = (property: string, value: any) => {
    if (!selectedPoint) return;
    
    console.log('🎨 PointControls handlePointUpdate:', {
      selectedPoint,
      property,
      value,
      path: `point.${selectedPoint.traceIndex}.${selectedPoint.pointIndex}.${property}`
    });
    
    // Send update with point coordinates
    onUpdate(`point.${selectedPoint.traceIndex}.${selectedPoint.pointIndex}.${property}`, value);
  };

  // Check if the selected trace supports editable points
  const isSupported = () => {
    if (!selectedPoint || !plotlyJson?.data?.[selectedPoint.traceIndex]) return false;
    const trace = plotlyJson.data[selectedPoint.traceIndex];
    // Use capabilities helper to check support
    return supportsPerPointColors(trace) || 
           (!trace.type && trace.mode?.includes('markers'));
  };

  const supportedTrace = isSupported();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <h4 className="font-medium text-sm">Points</h4>
      </div>

      {/* Point Selection Info */}
      {selectedPoint ? (
        supportedTrace ? (
          <div className="p-2 bg-accent rounded">
            <p className="text-xs font-medium">
              Point {selectedPoint.pointIndex + 1}, Trace {selectedPoint.traceIndex + 1}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {plotlyJson?.data?.[selectedPoint.traceIndex]?.name || `Series ${selectedPoint.traceIndex + 1}`}
            </p>
          </div>
        ) : (
          <div className="p-2 bg-muted rounded">
            <p className="text-xs text-muted-foreground">
              This trace type doesn't support individual point editing
            </p>
          </div>
        )
      ) : (
        <div className="p-2 bg-muted rounded">
          <p className="text-xs text-muted-foreground">
            Click a point in the plot to edit it
          </p>
        </div>
      )}

      {/* Controls - only enabled when a supported point is selected */}
      <div className={`space-y-4 ${!selectedPoint || !supportedTrace ? 'opacity-50 pointer-events-none' : ''}`}>
        
        {/* Fill Color */}
        <ColorPicker
          label="Fill Color"
          value={fillColor}
          onChange={(color) => {
            setFillColor(color);
            handlePointUpdate('marker.color', color);
          }}
          includeOpacity={false}
        />
        
        {/* Opacity */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-sm text-muted-foreground">
              Opacity
            </Label>
            <span className="text-xs text-muted-foreground">{Math.round(opacity * 100)}%</span>
          </div>
          <Slider
            value={[opacity]}
            onValueChange={([v]) => {
              setOpacity(v);
              handlePointUpdate('marker.opacity', v);
            }}
            min={0}
            max={1}
            step={0.05}
            className="w-full"
          />
        </div>

        {/* Size */}
        <div>
          <Label className="text-sm text-muted-foreground">
            Size
          </Label>
          <div className="mt-1 space-y-2">
            <Slider
              value={[pointSize]}
              onValueChange={([v]) => {
                setPointSize(v);
                handlePointUpdate('marker.size', v);
              }}
              min={2}
              max={30}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>2px</span>
              <span>{pointSize}px</span>
              <span>30px</span>
            </div>
          </div>
        </div>

        {/* Outline Color */}
        <ColorPicker
          label="Outline Color"
          value={outlineColor}
          onChange={(color) => {
            setOutlineColor(color);
            handlePointUpdate('marker.line.color', color);
          }}
          includeOpacity={false}
        />

        {/* Outline Width */}
        <div>
          <Label className="text-sm text-muted-foreground">
            Outline Width
          </Label>
          <div className="mt-1 space-y-2">
            <Slider
              value={[outlineWidth]}
              onValueChange={([v]) => {
                setOutlineWidth(v);
                handlePointUpdate('marker.line.width', v);
              }}
              min={0}
              max={5}
              step={0.5}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0px</span>
              <span>{outlineWidth}px</span>
              <span>5px</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}